import React, { useState, useEffect } from 'react';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PATH } from '../../../../constants/path';
import { CONTEXT_ROOT } from '../../../../constants';
import { EBON_TAB_IN_PROGRESS } from '../../../../constants/ebon';

import { fetchAccountEbonOrdersIfNeeded } from '../../../../services/account/ebons/actions';

import { createOrder } from '../../../../utils/ebonOrderUtils';
import { isOrderInProgress, isOrderConsumed } from '../../../../utils/ebonStatusUtils';

import EbonOrderCard from './EbonOrderCard';
import EbonsTabs from './EbonsTabs';
import HeadComponent from '../../../HeadComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './EbonsPage.scss';

const DISPLAY_STEP = 5;

const EbonsPage = ({ i18nPage, fetchAccountEbonOrdersIfNeeded, ebonOrders, fetching }) => {
    const [index, setIndex] = useState(DISPLAY_STEP);
    const [activeTab, setActiveTab] = useState(EBON_TAB_IN_PROGRESS);

    useEffect(() => {
        fetchAccountEbonOrdersIfNeeded();
    }, [fetchAccountEbonOrdersIfNeeded]);

    const filterEbonsByTab = orders => {
        if (activeTab === EBON_TAB_IN_PROGRESS) {
            return orders.filter(order => isOrderInProgress(order.ebonList));
        }
        return orders.filter(order => isOrderConsumed(order.ebonList));
    };

    const filteredOrders = filterEbonsByTab(ebonOrders);

    const renderSeeMoreButton = () => {
        if (filteredOrders.length >= DISPLAY_STEP && index < filteredOrders.length) {
            return (
                <div className="ebons-button">
                    <button
                        type="button"
                        id="more-ebons-button"
                        className="see-more-button"
                        title={i18nPage.SEE_MORE_EBONS}
                        onClick={handleSeeMoreClick}>
                        <span className="plus" aria-hidden="true">+</span>
                        <span className="text">{i18nPage.SEE_MORE_EBONS}</span>
                    </button>
                </div>
            );
        }
    };

    const handleSeeMoreClick = () => {
        setIndex(index + DISPLAY_STEP);
    };

    return (
        <main role="main" id="p-compte-bons-d-achat" style={{ marginTop: '32px' }}>
            <HeadComponent
                title={i18nPage.TITLE}
                description={i18nPage.DESCRIPTION}
                canonicalLink={`${CONTEXT_ROOT}${PATH.ACCOUNT_EBONS_PATH}`}
            />
            <EbonsTabs
                activeTab={activeTab}
                onTabChange={newTab => {
                    setActiveTab(newTab);
                    setIndex(DISPLAY_STEP);
                }}
            />
            <div
                role="tabpanel"
                id={
                    activeTab === EBON_TAB_IN_PROGRESS
                        ? 'tabpanel-in-progress'
                        : 'tabpanel-consumed'
                }
                aria-labelledby={
                    activeTab === EBON_TAB_IN_PROGRESS ? 'tab-in-progress' : 'tab-consumed'
                }>
                {fetching && <LoadingComponent />}
                {!fetching &&
                    filteredOrders.length > 0 &&
                    map(
                        filteredOrders.slice(0, index),
                        ({
                            items,
                            id,
                            date,
                            mergedItems,
                            billNumber,
                            partnerId,
                            vendorName,
                            ebonList,
                            state,
                            paymentRequest,
                            isEnterpriseCard
                        }) => (
                            <EbonOrderCard
                                key={id}
                                billNumber={billNumber}
                                items={items}
                                mergedItems={mergedItems}
                                id={id}
                                vendor={vendorName}
                                state={state}
                                partnerId={partnerId}
                                date={date}
                                ebonList={ebonList}
                                paymentRequest={paymentRequest}
                                isEnterpriseCard={isEnterpriseCard}
                            />
                        )
                    )}
                {!fetching && filteredOrders.length === 0 && (
                    <div className="no-ebons" role="status" aria-live="polite">
                        {activeTab === EBON_TAB_IN_PROGRESS
                            ? i18nPage.NO_EBONS_EN_COURS
                            : i18nPage.NO_EBONS_CONSOMMES}
                    </div>
                )}

                {renderSeeMoreButton()}
            </div>
        </main>
    );
};

EbonsPage.propTypes = {
    i18nPage: PropTypes.object.isRequired,
    fetchAccountEbonOrdersIfNeeded: PropTypes.func.isRequired,
    ebonOrders: PropTypes.array,
    fetching: PropTypes.bool
};

const mapStateToProps = state => {
    const ebonOrders = filterEbonOrders(state.account.myebons.ebonOrders);

    const ordersWithUserState = ebonOrders.map(order => {
        const orderWithCode = state.account.myebons.ebonOrderWithCodes.find(o => o.id === order.id);
        if (orderWithCode && orderWithCode.ebonList) {
            return { ...order, ebonList: orderWithCode.ebonList };
        }
        return order;
    });

    return {
        i18nPage: state.i18n.page.ACCOUNT.EBONS,
        ebonOrders: mapEbonOrders(ordersWithUserState, state.ebon.ebonPartners),
        fetching: state.account.myebons.fetching
    };
};

const filterEbonOrders = orders => {
    return orders.filter(order => order.ebonList.length > 0);
};

const mapEbonOrders = (orders, ebonPartners) => {
    return orders.map(o => createOrder({ ...o }, ebonPartners));
};

const mapDispatchToProps = {
    fetchAccountEbonOrdersIfNeeded
};

export default connect(mapStateToProps, mapDispatchToProps)(EbonsPage);



// WEBPACK FOOTER //
// ./src/components/pages/account/myebons/EbonsPage.js